export const TILE = {
  n: "normal",
  b: "boost",
  s: "slow",
  u: "supply",
  d: "deadly",
  c: "crumble",
  x: "block",
  i: "ice",
  f: "finish",
  ".": null,
};

const ROUND_STEP = 0.25;

function roundTarget(value) {
  return Math.round(value / ROUND_STEP) * ROUND_STEP;
}

export function decodePattern(code) {
  if (code.length !== 5) {
    throw new Error(`Road row "${code}" must contain exactly five lanes.`);
  }
  return [...code].map((token) => {
    if (!(token in TILE)) {
      throw new Error(`Unknown road tile token "${token}".`);
    }
    return TILE[token];
  });
}

function attachMeta(row, meta = {}) {
  for (const [key, value] of Object.entries(meta)) {
    if (value !== undefined) row[key] = value;
  }
  return row;
}

function mergeLandmark(row, landmark) {
  row._landmarks = [...(row._landmarks || []), landmark];
}

export class RoadWriter {
  constructor(id, worldId = "jungle") {
    this.id = id;
    this.worldId = worldId;
    this.rows = [];
  }

  add(code, count = 1, meta = {}) {
    for (let index = 0; index < count; index += 1) {
      const rowMeta =
        typeof meta === "function" ? meta(index, count, this.rows.length) : meta;
      this.rows.push(attachMeta(decodePattern(code), rowMeta));
    }
    return this;
  }

  gap(count = 1, meta = {}) {
    return this.add(".....", count, meta);
  }

  curve(code, count, from, to, meta = {}) {
    return this.add(code, count, (index, total) => {
      const amount = total <= 1 ? 1 : index / (total - 1);
      const extras = typeof meta === "function" ? meta(index, total) : meta;
      return { ...extras, _offset: from + (to - from) * amount };
    });
  }

  ramp(code, count, from, to, meta = {}) {
    return this.add(code, count, (index, total) => {
      const amount = total <= 1 ? 1 : index / (total - 1);
      const extras = typeof meta === "function" ? meta(index, total) : meta;
      return { ...extras, _elevation: from + (to - from) * amount };
    });
  }

  bendAndRamp(code, count, offsetFrom, offsetTo, heightFrom, heightTo, meta = {}) {
    return this.add(code, count, (index, total) => {
      const amount = total <= 1 ? 1 : index / (total - 1);
      const extras = typeof meta === "function" ? meta(index, total) : meta;
      return {
        ...extras,
        _offset: offsetFrom + (offsetTo - offsetFrom) * amount,
        _elevation: heightFrom + (heightTo - heightFrom) * amount,
      };
    });
  }

  markLast(landmark) {
    const row = this.rows.at(-1);
    if (row) mergeLandmark(row, landmark);
    return this;
  }

  setpiece(variant, side = 1, meta = {}) {
    return this.markLast({
      type: "world-setpiece",
      world: this.worldId,
      variant,
      side,
      ...meta,
    });
  }

  tree(code, lanes, meta = {}) {
    return this.add(code, 1, { ...meta, _trees: lanes });
  }

  hazard(code, count, type, lane = 2, meta = {}) {
    const start = this.rows.length;
    this.add(code, count, meta);
    const row = this.rows[start + Math.floor(Math.max(0, count - 1) / 2)];
    row._hazards = [...(row._hazards || []), { type, lane }];
    return this;
  }

  jump(landing = ".nnn.", gapRows = 1, meta = {}, launch = ".bbb.") {
    const previous = this.rows.at(-1);
    const launchMeta = {
      ...meta,
      _offset: Number(previous?._offset ?? meta._offset ?? 0),
      _elevation: Number(previous?._elevation ?? meta._elevation ?? 0),
    };
    this.add(launch, 1, launchMeta);
    this.gap(gapRows, (index, total) => {
      const amount = (index + 1) / (total + 1);
      return {
        ...meta,
        _offset:
          launchMeta._offset +
          (Number(meta._offset ?? launchMeta._offset) - launchMeta._offset) * amount,
        _elevation:
          launchMeta._elevation +
          (Number(meta._elevation ?? launchMeta._elevation) -
            launchMeta._elevation) *
            amount,
      };
    });
    this.add(landing, 3, meta);
    return this;
  }

  finish(meta = {}) {
    this.add("nnnnn", 3, meta);
    this.add("..f..", 1, {
      ...meta,
      _landmarks: [{ type: "portal", world: this.worldId, scale: 1 }],
      _finish: true,
    });
    return this;
  }
}

export function collectHazards(rows) {
  const hazards = [];
  rows.forEach((row, rowIndex) => {
    for (const hazard of row._hazards || []) {
      hazards.push({ ...hazard, row: rowIndex });
    }
  });
  return hazards;
}

export function analyseRoad(rows) {
  let finishTiles = 0;
  let gapRows = 0;
  let gapEvents = 0;
  let maxGap = 0;
  let currentGap = 0;
  let narrowRows = 0;
  let specialTiles = 0;
  let elevationGain = 0;
  let previousElevation = Number(rows[0]?._elevation || 0);

  for (const row of rows) {
    const lanes = row.filter(Boolean).length;
    finishTiles += row.filter((tile) => tile === "finish").length;
    specialTiles += row.filter(
      (tile) => tile && !["normal", "finish"].includes(tile),
    ).length;
    if (lanes > 0 && lanes <= 2) narrowRows += 1;
    if (lanes === 0) {
      gapRows += 1;
      currentGap += 1;
      if (currentGap === 1) gapEvents += 1;
      maxGap = Math.max(maxGap, currentGap);
    } else {
      currentGap = 0;
    }
    const elevation = Number(row._elevation || 0);
    elevationGain += Math.max(0, elevation - previousElevation);
    previousElevation = elevation;
  }

  return {
    rowCount: rows.length,
    finishTiles,
    gapRows,
    gapEvents,
    maxGap,
    narrowRows,
    specialTiles,
    elevationGain,
    hazardCount: collectHazards(rows).length,
  };
}

export function deriveStarTargets(rows, options) {
  const metrics = analyseRoad(rows);
  const speedMult = Math.max(0.8, Number(options.speedMult || 1));
  const difficulty = Math.max(1, Math.min(100, Number(options.difficulty || 1)));

  // Calibrated from the five recorded Jungle bests: launch overhead plus
  // near-max-speed row traversal, with explicit precision and hazard costs.
  const cleanRun =
    2.7 +
    (metrics.rowCount * 0.069) / speedMult +
    metrics.gapEvents * 0.16 +
    metrics.hazardCount * 0.2 +
    metrics.narrowRows * 0.006 +
    metrics.elevationGain * 0.055;
  const difficultyAllowance = 1.03 + difficulty * 0.0009;
  const star3 = roundTarget(cleanRun * difficultyAllowance);
  const star2 = roundTarget(star3 * (1.29 + difficulty * 0.0018));
  return { star3, star2, metrics };
}
